// Cognito credential fetchers. Two flavors:
//   - getUnauthCredentials({ region, identityPoolId })
//   - getAuthCredentials({ region, identityPoolId, userPoolId, idToken })
//
// Calls the Cognito Identity REST endpoint directly via fetch() rather than
// pulling in the AWS SDK. Keeps the deck-side bundle small (~5 KB vs ~150 KB).
// First call hits Cognito (~50 ms); subsequent calls within the cred TTL are
// served from sessionStorage.

const CACHE_KEY = 'rs_creds_v1';
const STORE     = (typeof sessionStorage !== 'undefined') ? sessionStorage : {
  getItem: () => null, setItem: () => {}, removeItem: () => {},
};

async function cognitoIdentityCall(region, target, body) {
  const res = await fetch(`https://cognito-identity.${region}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'X-Amz-Target': `AWSCognitoIdentityService.${target}`,
      'Content-Type': 'application/x-amz-json-1.1',
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Cognito ${target} ${res.status}: ${err}`);
  }
  return res.json();
}

function cacheKey(suffix) { return `${CACHE_KEY}:${suffix}`; }

function readCache(suffix) {
  try {
    const raw = STORE.getItem(cacheKey(suffix));
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (Date.now() < c.expiresAtMs - 60_000) return c;          // 60s safety margin
  } catch {}
  return null;
}

function writeCache(suffix, c) {
  try { STORE.setItem(cacheKey(suffix), JSON.stringify(c)); } catch {}
}

export function clearCredentialCache() {
  for (const k of ['unauth', 'auth']) STORE.removeItem(cacheKey(k));
}

export async function getUnauthCredentials({ region, identityPoolId }) {
  const cached = readCache('unauth');
  if (cached) return cached;

  const { IdentityId } = await cognitoIdentityCall(region, 'GetId', { IdentityPoolId: identityPoolId });
  const { Credentials } = await cognitoIdentityCall(region, 'GetCredentialsForIdentity', { IdentityId });

  const creds = {
    identityId:      IdentityId,
    accessKeyId:     Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken:    Credentials.SessionToken,
    expiresAtMs:     new Date(Credentials.Expiration * 1000).getTime(),
  };
  writeCache('unauth', creds);
  return creds;
}

export async function getAuthCredentials({ region, identityPoolId, userPoolId, idToken }) {
  // We don't cache auth creds across page loads — User Pool tokens expire and
  // re-fetching is cheap. (sessionStorage is per-tab and short-lived anyway.)
  const loginKey = `cognito-idp.${region}.amazonaws.com/${userPoolId}`;
  const { IdentityId } = await cognitoIdentityCall(region, 'GetId', {
    IdentityPoolId: identityPoolId,
    Logins: { [loginKey]: idToken },
  });
  const { Credentials } = await cognitoIdentityCall(region, 'GetCredentialsForIdentity', {
    IdentityId,
    Logins: { [loginKey]: idToken },
  });
  return {
    identityId:      IdentityId,
    accessKeyId:     Credentials.AccessKeyId,
    secretAccessKey: Credentials.SecretKey,
    sessionToken:    Credentials.SessionToken,
    expiresAtMs:     new Date(Credentials.Expiration * 1000).getTime(),
  };
}
